
import { ExclamationCircleIcon, TrashIcon } from "@heroicons/react/outline";
import { useEffect, useMemo, useState } from "react";
import { AbstractEmail } from "../interfaces";
import { EMAIL_REGEX } from "../utils";

type Props = {
  email: AbstractEmail,
  onChange: (field: string, value: any) => void,
  allowEditing: boolean
}

function WorkflowEmailTo(props: Props) {
  const [showBcc, setShowBcc] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const { email, onChange } = props;

  const handleEmailTo = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    onChange('emailTo', target.value)
  }

  const emptyTo = useMemo(() => !email.emailTo.trim().length, [email.emailTo]);

  const invalidTo = useMemo(() =>
    !email.emailTo.split(",").every(email => EMAIL_REGEX.test(email.trim())),
    [email.emailTo]
  );

  const invalidCc = useMemo(() =>
    showCc && !email.emailCc.split(",").every(email => EMAIL_REGEX.test(email.trim())),
    [email.emailCc, showCc]
  );

  const invalidBcc = useMemo(() =>
    showBcc && !email.emailBcc.split(",").every(email => EMAIL_REGEX.test(email.trim())),
    [email.emailBcc, showBcc]
  );

  useEffect(() => {
    setShowCc(email.emailCc.length > 0);
    setShowBcc(email.emailBcc.length > 0);
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <div className="flex items-center w-full relative mt-2 pr-14">
        <label className="w-20 font-gilroy font-medium text-gray-500" htmlFor="email-to" onClick={() => document.getElementById("email-to")?.focus()}>To</label>
        <input
          value={email.emailTo}
          onChange={handleEmailTo}
          placeholder="Type email addresses directly or insert {{Template Tags}}"
          className="w-full focus-visible:outline-none h-10 whitespace-nowrap overflow-hidden ml-2"
          id="email-to"
          disabled={!props.allowEditing}
        />
        <div className="absolute right-0 flex justify-end text-gray-500 bg-white pl-1">
          {(emptyTo || invalidTo) &&
            <ExclamationCircleIcon className="w-4 h-4 mr-2 mt-0.5 text-yellow " />
          }
          {(!showCc && props.allowEditing) && <button className="mr-2 font-semibold" onClick={() => setShowCc(true)} type="button">Cc</button>}
          {(!showBcc && props.allowEditing) && <button className="font-semibold" onClick={() => setShowBcc(true)} type="button">Bcc</button>}
        </div>
      </div>

      {showCc &&
        <div className="flex items-center w-full">
          <label className="w-20 font-gilroy font-medium text-gray-500" htmlFor="email-cc" onClick={() => document.getElementById("email-cc")?.focus()}>Cc</label>
          <div className="flex w-full items-center">
            <input
              value={email.emailCc}
              id="email-cc"
              onChange={({ target }) => onChange('emailCc', target.value)}
              className="w-full focus-visible:outline-none h-10 whitespace-nowrap overflow-hidden ml-2"
              disabled={!props.allowEditing}
            />
            {(showCc && email.emailCc.length > 0 && invalidCc) &&
              <ExclamationCircleIcon className="w-4 h-4 mt-0.5 text-yellow flex-shrink-0" />
            }
            <TrashIcon className="w-6 h-6 mx-2 cursor-pointer text-gray-500 hover:text-red" onClick={() => { setShowCc(false); onChange("emailCc", ""); }} />
          </div>
        </div>
      }

      {showBcc &&
        <div className="flex items-center w-full">
          <label className="w-20 font-gilroy font-medium text-gray-500" htmlFor="email-cc" onClick={() => document.getElementById("email-Bcc")?.focus()}>Bcc</label>
          <div className="flex w-full items-center">
            <input
              value={email.emailBcc}
              id="email-Bcc"
              onChange={({ target }) => onChange('emailBcc', target.value)}
              className="w-full focus-visible:outline-none h-10 whitespace-nowrap overflow-hidden ml-2"
              disabled={!props.allowEditing}
            />
            {(showBcc && email.emailBcc.length > 0 && invalidBcc) &&
              <ExclamationCircleIcon className="w-4 h-4 mt-0.5 text-yellow flex-shrink-0" />
            }
            <TrashIcon className="w-6 h-6 mx-2 cursor-pointer text-gray-500 hover:text-red" onClick={() => { setShowBcc(false); onChange("emailBcc", ""); }} />
          </div>
        </div>
      }
    </>
  );
}

export default WorkflowEmailTo;
